import { useCallback, useEffect, useMemo, useRef } from "react";

import { makeId } from "utils/serviceUtils/helpers";

const useDBEvents = <DbEvent>(broadcastChannel: string) => {
	const channel = useRef<null | BroadcastChannel>(null);
	const listeners = useRef<Map<string, (event: DbEvent) => void>>(new Map<string, (event: DbEvent) => void>());

	const notify = useCallback((event: DbEvent) => {
		listeners.current.forEach(listener => listener(event));
	}, []);

	const reinitChannel = useCallback(() => {
		channel.current =
			typeof window !== "undefined" && "BroadcastChannel" in window ? new BroadcastChannel(broadcastChannel) : null;

		if (channel.current) {
			channel.current.onmessage = (messageEvent: MessageEvent<DbEvent>) => {
				notify(messageEvent.data);
			};
		}
	}, [broadcastChannel, notify]);

	useEffect(() => {
		reinitChannel();
	}, [reinitChannel]);

	return useMemo(
		() => ({
			subscribe: (listener: (event: DbEvent) => void, id = makeId()) => {
				listeners.current.set(id, listener);
				return id;
			},
			unsubscribe: (id: string) => {
				listeners.current.delete(id);
			},
			emit: (event: DbEvent) => {
				try {
					if (channel.current) {
						channel.current?.postMessage(event);
					} else {
						notify(event);
					}
				} catch (e) {
					if ((e as Error)?.name === "InvalidStateError") {
						reinitChannel();
						channel.current?.postMessage(event);
					} else {
						console.error(e);
					}
				}
			},
			destroy: () => {
				channel.current?.close();
				listeners.current.clear();
			}
		}),
		[notify, reinitChannel]
	);
};

export default useDBEvents;
